import { useCallback, useMemo, useRef } from 'react';

import { Favorite, useCreateFavorite, useDeleteFavorite } from '@api/customers';

import { useCompareAndFavoriteContext } from '@context/compare';

import { useGetUserData } from './useGetUserData';

interface FavoriteHelperProps {
    offerId?: number;
    productId?: number;
    setIsAuthPopup: (val: boolean) => void;
}

const findFavoriteRecord = (
    favoriteProductsData: Favorite[] | undefined,
    productId: number | undefined,
    offerId: number | undefined
) =>
    favoriteProductsData?.find(
        item => (productId != null && item.product_id === productId) || (offerId != null && item.offer_id === offerId)
    );

export const useFavoriteHelper = ({ offerId, productId, setIsAuthPopup }: FavoriteHelperProps) => {
    const { user } = useGetUserData();
    const { favoriteProductsData } = useCompareAndFavoriteContext();
    const inFlightKeyRef = useRef<string | null>(null);

    const isFavorite = useMemo(
        () => Boolean(findFavoriteRecord(favoriteProductsData, productId, offerId)),
        [favoriteProductsData, offerId, productId]
    );

    const { mutateAsync: createFavorite } = useCreateFavorite();
    const { mutateAsync: deleteFavorite } = useDeleteFavorite();

    const favoriteHelper = useCallback(
        async (id: number) => {
            const flightKey = String(productId ?? offerId ?? id);

            if (inFlightKeyRef.current === flightKey) {
                return;
            }

            inFlightKeyRef.current = flightKey;

            try {
                const favoriteRecord = findFavoriteRecord(favoriteProductsData, productId, offerId);

                if (user) {
                    if (favoriteRecord) {
                        if (favoriteRecord.id != null) {
                            await deleteFavorite({
                                id: favoriteRecord.id,
                            });
                        }
                    } else {
                        await createFavorite({
                            offer_id: id,
                        });
                    }
                } else {
                    setIsAuthPopup(true);
                }
            } finally {
                inFlightKeyRef.current = null;
            }
        },
        [user, favoriteProductsData, productId, offerId, deleteFavorite, createFavorite, setIsAuthPopup]
    );

    return { favoriteHelper, isFavorite };
};
